import { useEffect, useState } from 'react';
import type { VaultFileEntry } from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';

/** Live snapshot of the vault file map (includes tombstoned entries). */
export function useVaultFiles(): Map<string, VaultFileEntry> {
  const [files, setFiles] = useState<Map<string, VaultFileEntry>>(() => {
    try {
      return vault.filesSnapshot();
    } catch {
      return new Map();
    }
  });
  useEffect(() => vault.onFiles(setFiles), []);
  return files;
}

/** Re-render whenever the index worker reports new content. */
export function useIndexRev(): number {
  const [rev, setRev] = useState(index.rev);
  useEffect(() => index.onChange(setRev), []);
  return rev;
}

// ---------------------------------------------------------------- MRU notes

const RECENT_KEY = 'rbv.recentNotes';
const RECENT_MAX = 30;

export function getRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function recordRecent(id: string): void {
  try {
    const list = [id, ...getRecents().filter((x) => x !== id)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // storage unavailable — recents are best-effort
  }
}

// ------------------------------------------------- persisted string sets

export function loadStringSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveStringSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // best-effort persistence
  }
}
